import React from 'react';
import dynamic from 'next/dynamic';
import { getLog } from '@aurora/shared-utils/log';
import type { ItemType } from '../../../types/enums';
import { IdeaStatusViewVariant } from '../../../types/enums';
import type { IdeaStatusDetailsFragment } from '../../../types/graphql-types';
import mergeVariantProps from './IdeaStatusViewDefaultProps';
import type { ItemViewFC, ItemViewVariantFC } from '../../entities/types';
import type { IdeaStatusViewCommonProps } from './types';

const log = getLog(module);

const IdeaStatusViewInline = dynamic(() => import('./IdeaStatusViewInline/IdeaStatusViewInline'));
const IdeaStatusViewCard = dynamic(() => import('./IdeaStatusViewCard/IdeaStatusViewCard'));

const variantToComponentMap: Record<
  IdeaStatusViewVariant,
  ItemViewVariantFC<IdeaStatusDetailsFragment, ItemType.IDEA_STATUS, IdeaStatusViewVariant>
> = {
  [IdeaStatusViewVariant.CARD]: IdeaStatusViewCard,
  [IdeaStatusViewVariant.INLINE]: IdeaStatusViewInline
};

/**
 * The base idea status view component that renders the idea status view specified by
 * the <code>variant</code> parameter. This component should be used anywhere an idea status
 * with its idea count is rendered.
 *
 * @author rishabh.jivan
 */
const IdeaStatusView: ItemViewFC<
  IdeaStatusDetailsFragment,
  ItemType.IDEA_STATUS,
  IdeaStatusViewVariant
> = ({ entity: ideaStatus, variant, className }) => {
  const IdeaStatusViewComponent = variantToComponentMap[variant.type.toString()];

  if (IdeaStatusViewComponent) {
    const defaultProps = mergeVariantProps<IdeaStatusViewCommonProps>(variant);

    return (
      <IdeaStatusViewComponent
        entity={ideaStatus}
        className={className}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{ ...defaultProps, ...variant.props }}
      />
    );
  }
  log.warn('No registered idea status view for variant type [%s]', variant.type);
  return null;
};

export default IdeaStatusView;
